<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import MetaLeadFormsAPI from 'dashboard/api/metaLeadForms';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inboxes: { type: Array, default: () => [] },
  // lista de formulários já existentes da conta, usada p/ pré-preencher inbox/página/
  // etiquetas de um formulário NOVO (são "por conta", repetem entre os forms do cliente).
  forms: { type: Array, default: () => [] },
});
const emit = defineEmits(['saved']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const saving = ref(false);
const editingId = ref(null);
const fetchingForms = ref(false);
const remoteForms = ref([]);
// Busca por ID do formulário (fluxo recomendado, self-serve)
const fetchingForm = ref(false);
const fetchedFormInfo = ref(null);
// defaults de instância (ENV/InstallationConfig) p/ política/texto
const instanceDefaults = ref(null);

// id estável por linha de mapeamento (key do v-for): evita reuso de DOM/foco errado
// ao remover linha do meio com splice.
let rowSeq = 0;
const newRow = (field = '', attr = '') => ({ id: (rowSeq += 1), field, attr });
const newRule = (field = '', op = 'equals', value = '') => ({
  id: (rowSeq += 1),
  field,
  op,
  value,
});

// Operadores casam com rule_matches? do lead_capture_controller (backend).
const OPERATORS = computed(() => [
  { value: 'equals', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.OPERATORS.EQUALS') },
  { value: 'not_equals', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.OPERATORS.NOT_EQUALS') },
  { value: 'contains', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.OPERATORS.CONTAINS') },
  { value: 'gt', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.OPERATORS.GT') },
  { value: 'gte', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.OPERATORS.GTE') },
  { value: 'lt', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.OPERATORS.LT') },
  { value: 'lte', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.OPERATORS.LTE') },
  { value: 'in', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.OPERATORS.IN') },
  { value: 'not_in', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.OPERATORS.NOT_IN') },
]);
const isListOp = op => op === 'in' || op === 'not_in';

const blankForm = () => ({
  name: '',
  pageId: '',
  formId: '',
  inboxId: '',
  message: '',
  mapping: [], // [{ field, attr }]
  aiContinue: false,
  sendProactive: false,
  privateNote: false,
  privateNoteIntro: '',
  rules: [], // [{ field, op, value }]
  qualifiedLabel: '',
  disqualifiedLabel: '',
  skipIfUnqualified: false,
  welcomeImage: null, // { filename } quando já anexada
  welcomeImageBlobId: null,
  removeWelcomeImage: false,
  uploadingImage: false,
});

const form = ref(blankForm());

const conversationAttributes = computed(
  () => store.getters['attributes/getAttributesByModel']?.('conversation_attribute') || []
);
// Campos personalizados (conversa) como opções de SELECT: a pessoa escolhe, não
// digita a chave à mão.
const attributeOptions = computed(() =>
  conversationAttributes.value
    .map(a => ({ key: a?.attribute_key, name: a?.attribute_display_name || a?.attribute_key }))
    .filter(o => o.key)
);
// Campos que ESTE formulário grava (destinos do mapeamento) viram as opções de
// "campo" da regra de qualificação (qualifica pelo que você capturou).
const mappedAttrOptions = computed(() => [
  ...new Set(form.value.mapping.map(m => (m.attr || '').trim()).filter(Boolean)),
]);
// Etiquetas da conta como SELECT (qualificado/desqualificado).
const labelsList = computed(() => store.getters['labels/getLabels'] || []);
// Opções do campo da regra = campos mapeados + os já usados nas regras (não perde o
// valor ao editar um form salvo).
const qualifyFieldOptions = computed(() => [
  ...new Set([
    ...mappedAttrOptions.value,
    ...form.value.rules.map(r => (r.field || '').trim()).filter(Boolean),
  ]),
]);
// Títulos de etiqueta (existentes + os já salvos no form, p/ não sumir ao editar).
const labelTitles = computed(() => {
  const titles = labelsList.value.map(l => l.title).filter(Boolean);
  const extra = [form.value.qualifiedLabel, form.value.disqualifiedLabel].filter(
    v => v && !titles.includes(v)
  );
  return [...new Set([...titles, ...extra])];
});

const selectedInbox = computed(() =>
  props.inboxes.find(i => i.id === Number(form.value.inboxId))
);
const proactiveSupported = computed(() => {
  const type = selectedInbox.value?.channel_type;
  return type === 'Channel::Api' || type === 'Channel::WhatsappWeb';
});

const reset = (endpoint = null) => {
  remoteForms.value = [];
  fetchedFormInfo.value = null;
  if (endpoint) {
    editingId.value = endpoint.id;
    const cfg = endpoint.capture_config || {};
    const fm = cfg.field_mapping && typeof cfg.field_mapping === 'object' ? cfg.field_mapping : {};
    const bool = v => v === true || v === 'true';
    form.value = {
      ...blankForm(),
      name: endpoint.name || '',
      pageId: cfg.meta_page_id || '',
      formId: cfg.meta_form_id || '',
      inboxId: endpoint.inbox_id || '',
      message: endpoint.message_template || '',
      mapping: Object.entries(fm).map(([field, attr]) => newRow(field, attr)),
      aiContinue: bool(cfg.ai_continue),
      sendProactive: endpoint.send_proactive === true,
      privateNote: bool(cfg.private_note),
      privateNoteIntro: cfg.private_note_intro || '',
      rules: Array.isArray(cfg.qualification_rules)
        ? cfg.qualification_rules.map(r => newRule(r.field, r.op || 'equals', r.value))
        : [],
      qualifiedLabel: cfg.qualified_label || '',
      disqualifiedLabel: cfg.disqualified_label || '',
      skipIfUnqualified: bool(cfg.skip_proactive_if_unqualified),
      welcomeImage: endpoint.welcome_image || null,
    };
  } else {
    editingId.value = null;
    const d = instanceDefaults.value || {};
    // herda do ÚLTIMO formulário da conta (inbox/página/etiquetas = "por conta")
    const last = props.forms?.[props.forms.length - 1];
    const lc = last?.capture_config || {};
    form.value = {
      ...blankForm(),
      pageId: lc.meta_page_id || '',
      inboxId: last?.inbox_id || '',
      qualifiedLabel: lc.qualified_label || '',
      disqualifiedLabel: lc.disqualified_label || '',
      // herda dos PADRÕES da instância (política/texto)
      message: d.message_template || '',
      aiContinue: d.ai_continue ?? true,
      sendProactive: d.send_proactive ?? false,
      privateNote: d.private_note ?? true,
      privateNoteIntro: d.private_note_intro || '',
      skipIfUnqualified: d.skip_proactive_if_unqualified ?? false,
    };
  }
};

const open = (endpoint = null) => {
  reset(endpoint);
  dialogRef.value?.open();
};

onMounted(async () => {
  if (!conversationAttributes.value.length) store.dispatch('attributes/get');
  if (!labelsList.value.length) store.dispatch('labels/get');
  try {
    const { data } = await MetaLeadFormsAPI.defaults();
    instanceDefaults.value = data || null;
  } catch (e) {
    instanceDefaults.value = null;
  }
});

const fetchForms = async () => {
  if (!form.value.pageId) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ALERTS.PAGE_ID_FIRST'));
    return;
  }
  fetchingForms.value = true;
  try {
    const { data } = await MetaLeadFormsAPI.forms(form.value.pageId);
    remoteForms.value = Array.isArray(data?.data) ? data.data : [];
    if (!remoteForms.value.length) {
      useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ALERTS.NO_FORMS'));
    }
  } catch (e) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ALERTS.FETCH_FORMS_ERROR'));
  } finally {
    fetchingForms.value = false;
  }
};

// Fluxo recomendado: cola o ID do form -> descobre a página + lista os campos +
// pré-preenche o mapeamento (padrão automático, custom sugerido). Self-serve: cada
// cliente usa o ID do form DELE; o backend deriva a página e bloqueia páginas de
// outras contas.
const fetchFormById = async () => {
  const fid = (form.value.formId || '').trim();
  if (!fid) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ALERTS.FORM_ID_FIRST'));
    return;
  }
  fetchingForm.value = true;
  try {
    const { data } = await MetaLeadFormsAPI.fetchForm(fid);
    form.value.pageId = data.page_id || form.value.pageId;
    form.value.formId = data.form_id || fid;
    const sug = data.suggested_mapping || {};
    form.value.mapping = (data.fields || []).map(f => newRow(f.key, sug[f.key] || ''));
    fetchedFormInfo.value = {
      name: data.form_name,
      page: data.page_name,
      count: (data.fields || []).length,
    };
  } catch (e) {
    fetchedFormInfo.value = null;
    useAlert(
      e?.response?.data?.error || t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ALERTS.FETCH_FORM_ERROR')
    );
  } finally {
    fetchingForm.value = false;
  }
};

const addMappingRow = () => form.value.mapping.push(newRow());
const removeMappingRow = i => form.value.mapping.splice(i, 1);

const addRule = () => form.value.rules.push(newRule());
const removeRule = i => form.value.rules.splice(i, 1);

const handleImage = async event => {
  const file = event.target?.files?.[0];
  if (!file) return;
  form.value.uploadingImage = true;
  try {
    const { blobId } = await uploadFile(file);
    form.value.welcomeImageBlobId = blobId;
    form.value.welcomeImage = { filename: file.name };
    form.value.removeWelcomeImage = false;
  } catch (e) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ALERTS.IMAGE_ERROR'));
  } finally {
    form.value.uploadingImage = false;
    if (event.target) event.target.value = '';
  }
};
const clearImage = () => {
  form.value.welcomeImage = null;
  form.value.welcomeImageBlobId = null;
  form.value.removeWelcomeImage = true;
};

const buildPayload = () => {
  const fieldMapping = {};
  form.value.mapping.forEach(({ field, attr }) => {
    const f = (field || '').trim();
    const a = (attr || '').trim();
    if (f && a) fieldMapping[f] = a;
  });
  const rules = form.value.rules
    .map(r => ({ field: (r.field || '').trim(), op: r.op, value: (r.value || '').trim() }))
    .filter(r => r.field && r.op);
  const payload = {
    name: form.value.name,
    inbox_id: Number(form.value.inboxId) || null,
    message_template: form.value.message,
    send_proactive: form.value.sendProactive,
    capture_config: {
      meta_page_id: form.value.pageId.trim(),
      meta_form_id: form.value.formId.trim(),
      field_mapping: fieldMapping,
      ai_continue: form.value.aiContinue,
      private_note: form.value.privateNote,
      private_note_intro: form.value.privateNoteIntro.trim(),
      qualification_rules: rules,
      qualified_label: form.value.qualifiedLabel.trim(),
      disqualified_label: form.value.disqualifiedLabel.trim(),
      skip_proactive_if_unqualified: form.value.skipIfUnqualified,
    },
  };
  if (form.value.welcomeImageBlobId) payload.welcome_image_blob_id = form.value.welcomeImageBlobId;
  if (form.value.removeWelcomeImage) payload.remove_welcome_image = true;
  return payload;
};

const handleSubmit = async () => {
  if (saving.value) return;
  if (!form.value.name?.trim()) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ALERTS.NAME_REQUIRED'));
    return;
  }
  if (!form.value.pageId?.trim()) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ALERTS.PAGE_REQUIRED'));
    return;
  }
  if (!form.value.inboxId) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ALERTS.INBOX_REQUIRED'));
    return;
  }
  saving.value = true;
  try {
    const payload = { meta_lead_form: buildPayload() };
    if (editingId.value) {
      await MetaLeadFormsAPI.update(editingId.value, payload);
    } else {
      await MetaLeadFormsAPI.create(payload);
    }
    emit('saved');
    dialogRef.value?.close();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ALERTS.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const messageHint = computed(() =>
  t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.MESSAGE_HINT', { fields: '{nome}, {email}, {telefone}' })
);

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="editingId ? $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.TITLE_EDIT') : $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.TITLE_NEW')"
    :show-confirm-button="false"
    :cancel-button-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.CLOSE')"
    width="2xl"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.name"
        :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.NAME')"
        :placeholder="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.NAME_PLACEHOLDER')"
      />

      <!-- Recomendado: configurar pelo ID do formulário (descobre a página e lista os campos sozinho) -->
      <div class="flex flex-col gap-2 p-3 rounded-xl border border-n-weak bg-n-alpha-1">
        <div class="flex items-end gap-2">
          <Input
            v-model="form.formId"
            :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.FORM_ID')"
            :placeholder="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.FORM_ID_PLACEHOLDER')"
            class="flex-1"
          />
          <Button
            type="button"
            :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.FETCH_FIELDS')"
            color="blue"
            size="sm"
            :is-loading="fetchingForm"
            @click="fetchFormById"
          />
        </div>
        <p v-if="fetchedFormInfo" class="text-xs text-n-teal-11">
          <span class="i-ri-check-line size-3.5 align-text-bottom" />
          {{
            $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.FETCHED', {
              name: fetchedFormInfo.name,
              page: fetchedFormInfo.page,
              count: fetchedFormInfo.count,
            })
          }}
        </p>
        <p v-else class="text-xs text-n-slate-10">
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.FETCH_HINT') }}
        </p>
      </div>

      <!-- Página + formulários (alternativo) -->
      <div class="flex flex-col gap-2">
        <div class="flex items-end gap-2">
          <Input
            v-model="form.pageId"
            :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.PAGE_ID')"
            placeholder="1234567890"
            class="flex-1"
          />
          <Button
            type="button"
            :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.FETCH_FORMS')"
            variant="faded"
            color="slate"
            size="sm"
            :is-loading="fetchingForms"
            @click="fetchForms"
          />
        </div>
        <div v-if="remoteForms.length" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12" for="meta-form-remote">{{
            $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.FORM')
          }}</label>
          <select
            id="meta-form-remote"
            v-model="form.formId"
            class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
          >
            <option value="">{{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ANY_FORM_OF_PAGE') }}</option>
            <option v-for="rf in remoteForms" :key="rf.id" :value="rf.id">
              {{ rf.name || rf.id }}{{ rf.status ? ` (${rf.status})` : '' }}
            </option>
          </select>
        </div>
        <Input
          v-else
          v-model="form.formId"
          :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.FORM_ID_OPTIONAL')"
          :placeholder="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.FORM_ID_OPTIONAL_PLACEHOLDER')"
        />
      </div>

      <!-- Inbox -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12" for="meta-form-inbox">{{
          $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.INBOX')
        }}</label>
        <select
          id="meta-form-inbox"
          v-model="form.inboxId"
          class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
        >
          <option value="">{{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.SELECT') }}</option>
          <option v-for="i in inboxes" :key="i.id" :value="i.id">
            {{ i.name }}
          </option>
        </select>
      </div>

      <!-- Mensagem -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12" for="meta-form-message">
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.MESSAGE') }}
        </label>
        <textarea
          id="meta-form-message"
          v-model="form.message"
          rows="3"
          :placeholder="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.MESSAGE_PLACEHOLDER', { name: '{nome}' })"
          class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
        />
        <p class="text-xs text-n-slate-10">
          {{ messageHint }}
        </p>
      </div>

      <!-- Imagem de boas-vindas -->
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.WELCOME_IMAGE') }}
        </span>
        <div class="flex items-center gap-2 flex-wrap">
          <label
            class="inline-flex items-center gap-1.5 text-xs text-n-slate-11 cursor-pointer px-2 py-1 rounded-md border border-n-weak hover:bg-n-alpha-2"
          >
            <span class="i-ri-image-line size-3.5" />
            {{ form.uploadingImage ? $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.UPLOADING') : $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ATTACH_IMAGE') }}
            <input
              type="file"
              accept="image/*"
              class="hidden"
              :disabled="form.uploadingImage"
              @change="handleImage"
            />
          </label>
          <span
            v-if="form.welcomeImage"
            class="inline-flex items-center gap-1 text-xs text-n-slate-11"
          >
            <span class="i-ri-check-line size-3.5 text-n-teal-11" />
            {{ form.welcomeImage.filename }}
            <button
              type="button"
              class="inline-flex items-center justify-center size-11 text-n-slate-10 hover:text-n-ruby-11 shrink-0"
              :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.REMOVE_IMAGE')"
              @click="clearImage"
            >
              <span class="i-ri-close-line size-3.5" />
            </button>
          </span>
        </div>
        <p class="text-xs text-n-slate-10">
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.WELCOME_IMAGE_HINT') }}
        </p>
      </div>

      <!-- Mapeamento de campos -->
      <div class="flex flex-col gap-2 p-3 rounded-xl border border-n-weak">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.MAPPING') }}
          </span>
          <Button
            type="button"
            :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ADD')"
            variant="faded"
            color="slate"
            size="xs"
            icon="i-ri-add-line"
            @click="addMappingRow"
          />
        </div>
        <div
          v-for="(row, i) in form.mapping"
          :key="row.id"
          class="flex items-center gap-2"
        >
          <input
            v-model="row.field"
            :placeholder="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.MAPPING_FIELD_PLACEHOLDER')"
            :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.MAPPING_FIELD')"
            class="flex-1 px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
          />
          <span class="i-ri-arrow-right-line size-4 text-n-slate-10 shrink-0" />
          <select
            v-model="row.attr"
            :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.MAPPING_ATTR')"
            class="flex-1 px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
          >
            <option value="">{{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.MAPPING_ATTR_PLACEHOLDER') }}</option>
            <option v-for="o in attributeOptions" :key="o.key" :value="o.key">
              {{ o.name }}
            </option>
          </select>
          <button
            type="button"
            class="inline-flex items-center justify-center size-11 text-n-slate-10 hover:text-n-ruby-11 shrink-0"
            :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.REMOVE_ROW')"
            @click="removeMappingRow(i)"
          >
            <span class="i-ri-close-line size-4" />
          </button>
        </div>
        <p
          v-if="form.mapping.length && !attributeOptions.length"
          class="text-xs text-n-amber-11"
        >
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.NO_ATTRIBUTES') }}
        </p>
        <p v-if="!form.mapping.length" class="text-xs text-n-slate-10">
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.NO_MAPPING') }}
        </p>
      </div>

      <!-- Avançado: política/texto vêm dos padrões; fica colapsado p/ não poluir -->
      <details class="rounded-xl border border-n-weak">
        <summary class="cursor-pointer select-none px-3 py-2 text-sm font-medium text-n-slate-12">
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ADVANCED') }}
          <span class="text-xs font-normal text-n-slate-10">{{
            $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ADVANCED_HINT')
          }}</span>
        </summary>
        <div class="flex flex-col gap-4 p-3 pt-1">
      <!-- Qualificação de contatos -->
      <div class="flex flex-col gap-2 p-3 rounded-xl border border-n-weak">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.QUALIFY') }}
          </span>
          <Button
            type="button"
            :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.ADD_RULE')"
            variant="faded"
            color="slate"
            size="xs"
            icon="i-ri-add-line"
            @click="addRule"
          />
        </div>
        <div
          v-for="(rule, i) in form.rules"
          :key="rule.id"
          class="flex flex-col gap-1 p-2 rounded-md border border-n-weak"
        >
          <div class="flex items-center gap-2">
            <select
              v-model="rule.field"
              :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.RULE_FIELD_LABEL')"
              class="flex-1 px-2 py-1.5 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
            >
              <option value="">{{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.RULE_FIELD') }}</option>
              <option v-for="k in qualifyFieldOptions" :key="k" :value="k">{{ k }}</option>
            </select>
            <select
              v-model="rule.op"
              :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.RULE_OPERATOR')"
              class="px-2 py-1.5 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
            >
              <option v-for="o in OPERATORS" :key="o.value" :value="o.value">
                {{ o.label }}
              </option>
            </select>
            <button
              type="button"
              class="inline-flex items-center justify-center size-11 text-n-slate-10 hover:text-n-ruby-11 shrink-0"
              :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.REMOVE_RULE')"
              @click="removeRule(i)"
            >
              <span class="i-ri-close-line size-4" />
            </button>
          </div>
          <textarea
            v-if="isListOp(rule.op)"
            v-model="rule.value"
            rows="2"
            :placeholder="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.RULE_VALUES')"
            :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.RULE_VALUE')"
            class="w-full px-2 py-1.5 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
          />
          <input
            v-else
            v-model="rule.value"
            :placeholder="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.RULE_VALUE')"
            :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.RULE_VALUE')"
            class="w-full px-2 py-1.5 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
          />
        </div>
        <p v-if="!form.rules.length" class="text-xs text-n-slate-10">
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.NO_RULES') }}
        </p>
        <p
          v-if="form.rules.length && !mappedAttrOptions.length"
          class="text-xs text-n-amber-11"
        >
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.MAP_TO_QUALIFY') }}
        </p>
        <template v-if="form.rules.length">
          <div class="flex items-center gap-2">
            <div class="flex-1 flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12" for="meta-form-qualified">{{
                $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.QUALIFIED_LABEL')
              }}</label>
              <select
                id="meta-form-qualified"
                v-model="form.qualifiedLabel"
                class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
              >
                <option value="">{{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.QUALIFIED_DEFAULT') }}</option>
                <option v-for="t in labelTitles" :key="t" :value="t">{{ t }}</option>
              </select>
            </div>
            <div class="flex-1 flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12" for="meta-form-disqualified">{{
                $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.DISQUALIFIED_LABEL')
              }}</label>
              <select
                id="meta-form-disqualified"
                v-model="form.disqualifiedLabel"
                class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
              >
                <option value="">{{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.DISQUALIFIED_DEFAULT') }}</option>
                <option v-for="t in labelTitles" :key="t" :value="t">{{ t }}</option>
              </select>
            </div>
          </div>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="form.skipIfUnqualified" type="checkbox" />
            {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.SKIP_IF_UNQUALIFIED') }}
          </label>
        </template>
      </div>

      <!-- Nota privada -->
      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.privateNote" type="checkbox" />
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.PRIVATE_NOTE') }}
        </label>
        <Input
          v-if="form.privateNote"
          v-model="form.privateNoteIntro"
          :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.PRIVATE_NOTE_TITLE')"
          :placeholder="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.PRIVATE_NOTE_PLACEHOLDER')"
        />
      </div>

      <!-- IA + envio -->
      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.aiContinue" type="checkbox" />
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.AI_CONTINUE') }}
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.sendProactive" type="checkbox" />
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.SEND_PROACTIVE') }}
        </label>
        <p
          v-if="form.sendProactive && form.inboxId && !proactiveSupported"
          class="text-xs text-n-ruby-11"
        >
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.PROACTIVE_UNSUPPORTED') }}
        </p>
      </div>
        </div>
      </details>

      <div class="flex gap-2 pt-1">
        <Button
          type="button"
          :label="editingId ? $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.SAVE') : $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIG.CREATE')"
          size="sm"
          :is-loading="saving"
          @click="handleSubmit"
        />
      </div>
    </div>
  </Dialog>
</template>
